/**
 * Money is integer bani everywhere. Format only at the edge of the UI.
 *
 * `currencyDisplay: "code"` — SO IT SAYS MDL AND NOT "L".
 *
 * The default is `"symbol"`, and CLDR's symbol for MDL in ro-MD is the single
 * letter `L`. So every price in the app read "135,00 L": a letter that names no
 * currency anyone transacts in, sits one keystroke from the litre, and appears
 * on no bank statement, no invoice and no fiscal receipt in Moldova. The ISO
 * code is MDL, the receipt email has always said MDL (worker/lib/receipt.ts),
 * and the menu footer promises "toate sumele în MDL" — the prices themselves
 * were the only thing disagreeing.
 *
 * The separator Intl emits between number and code is U+00A0, a non-breaking
 * space. That is wanted: "135,00" and "MDL" must never be split across a line
 * on a narrow phone.
 */
export function lei(minor: number | null | undefined): string {
  if (minor == null) return "—";
  return new Intl.NumberFormat("ro-MD", {
    style: "currency",
    currency: "MDL",
    currencyDisplay: "code",
    minimumFractionDigits: 2,
  }).format(minor / 100);
}

/** The house writes times with a dot: 18.00, 23.00. Never a colon. */
export function chisinauTime(epochSec: number): string {
  return new Intl.DateTimeFormat("ro-RO", {
    timeZone: "Europe/Chisinau",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(epochSec * 1000)
    .replace(":", ".");
}

/**
 * A date, in Chisinau, THE WAY MOLDOVA WRITES ONE: „10.08.2026".
 *
 * Day, month, year, separated by dots — the form on every fiscal receipt, bank
 * statement and buletin in the country. It used to spell the month out
 * („10 august 2026"), and for a while that pulled the function through a
 * three-language detour: the guest order page is trilingual, so a Russian
 * cardholder was reading a Romanian month name inside a Russian sentence until
 * `lang` was added to pick between ro-MD, ru-MD and en-GB month spellings.
 *
 * The national numeric form dissolves that whole problem instead of solving
 * it: digits are the one spelling all three languages share (ru-MD writes
 * 10.08.2026 with the same dots), so every reader of every page now gets the
 * same bytes for the same instant. `lang` STAYS IN THE SIGNATURE and selects
 * nothing — callers pass it, the worker twin (`chisinauDate` in
 * worker/lib/core.ts) has the same shape, and one product must not disagree
 * with itself about the date of one payment.
 *
 * `chisinauTime` needs no argument either way: `hour12: false` plus the
 * explicit `.replace(":", ".")` render „21.30" identically in every locale.
 */
export function chisinauDate(epochSec: number, _lang: string = "ro"): string {
  return new Intl.DateTimeFormat("ro-MD", {
    timeZone: "Europe/Chisinau",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(epochSec * 1000);
}

/** 800 -> "8%" */
export function vatLabel(bp: number): string {
  return `${bp / 100}%`;
}

/** Gross bani + rate in basis points -> the VAT portion, integer arithmetic. */
export function vatPortion(grossMinor: number, rateBp: number): number {
  return grossMinor - Math.round((grossMinor * 10000) / (10000 + rateBp));
}

/**
 * Margin, in bani, NET OF OUTPUT VAT.
 *
 * `price_minor` is VAT-inclusive — `migrations/0002_catalog.sql:53` says so
 * explicitly, and `vatSplit()` in worker/lib/core.ts splits gross to net the
 * same way. Subtracting a purchase cost from a gross price therefore counted
 * the VAT the house merely collects on the state's behalf as if it were the
 * house's own revenue, overstating every margin by the whole rate: 8% on food,
 * 20% on wine. On a 45-lei glass costing 20 lei that is 56% reported against
 * 46% real, and the error is largest exactly on the alcohol that carries the
 * evening.
 *
 * Whether `products.unit_cost_minor` ("bani, what we paid") is recorded net or
 * gross of deductible input VAT is an ACCOUNTING CONVENTION, not a technical
 * fact — so it is a house setting (`cost_basis`), editable in the admin, rather
 * than something hardcoded here or blocked on an answer. See
 * worker/lib/settings.ts.
 *
 * When the basis is "gross", the cost is netted using the PRODUCT'S OWN rate.
 * That is an approximation and worth naming: input VAT on a purchase is the
 * supplier's rate, which for goods bought to resell is normally the same rate,
 * but need not be. It is right for wine bought and sold at 20% and for food at
 * 8%; it would be wrong for an input taxed differently from the output. When
 * the procurement module lands and real supplier invoices carry their own rate,
 * this reads that instead of guessing.
 */
export type CostBasis = "net" | "gross";

function netCost(costMinor: number, vatRateBp: number, basis: CostBasis): number {
  return basis === "gross" ? costMinor - vatPortion(costMinor, vatRateBp) : costMinor;
}

/**
 * `costMinor` accepts `undefined` as well as `null`, and both mean THE SAME
 * THING: the margin is unknown.
 *
 * `null` means no cost was ever recorded. `undefined` means D-09 removed the
 * field before the payload left the Worker, because this viewer does not hold
 * `cost.read`. The caller cannot tell them apart and must not try — what
 * matters is the invariant both share, which is that NEITHER MAY BE COALESCED
 * TO ZERO. A zero cost is a 100% margin, and a screen that quietly reports one
 * is worse than a screen that admits it does not know.
 */
export function margin(
  priceMinor: number,
  costMinor: number | null | undefined,
  vatRateBp: number,
  costBasis: CostBasis = "net"
): number | null {
  if (costMinor == null) return null;
  return priceMinor - vatPortion(priceMinor, vatRateBp) - netCost(costMinor, vatRateBp, costBasis);
}

/** See `margin` above on why `undefined` is accepted and what it means. */
export function marginPct(
  priceMinor: number,
  costMinor: number | null | undefined,
  vatRateBp: number,
  costBasis: CostBasis = "net"
): number | null {
  const net = priceMinor - vatPortion(priceMinor, vatRateBp);
  if (costMinor == null || net <= 0) return null;
  return Math.round(((net - netCost(costMinor, vatRateBp, costBasis)) / net) * 100);
}

/* --------------------------------------------- the national date and time */

/** A day key, `"2026-08-15"`, worn the national way: „15.08.2026". */
export function roDate(key: string): string {
  return `${key.slice(8, 10)}.${key.slice(5, 7)}.${key.slice(0, 4)}`;
}

/**
 * What a person types into a time field, read the house's way: „17.00",
 * „17:00", „1700" and a bare „17" all name the same moment of the wall clock.
 * Returns the canonical `"HH:mm"` the wire format wants, or null for anything
 * that is not a time of day — `25.00` is not a late hour, it is a mistake,
 * and a field that quietly wraps it to 1.00 would move a real deadline.
 *
 * The empty string is NOT a time and returns null like the rest: callers for
 * whom "" means "no bound at all" (the ordering window) test for it before
 * calling, because that distinction is theirs, not the parser's.
 */
export function parseRoTime(text: string): string | null {
  const m = text.trim().match(/^(\d{1,2})(?:[.:]?(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = m[2] == null ? 0 : Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/* ------------------------------------------------- editing a date and time */

/**
 * Epoch seconds -> the value an `<input type="datetime-local">` wants,
 * expressed in CHISINAU time: "2026-08-15T17:00".
 *
 * The naive version is `new Date(t*1000).toISOString().slice(0,16)`, and it is
 * wrong twice: it renders UTC, and it silently shifts the displayed hour by two
 * or three depending on the season. A host editing "17.00" would see "14:00"
 * and correct it, moving the real event.
 *
 * `sv-SE` because it formats as `YYYY-MM-DD HH:mm:ss` — ISO order without the
 * `T` — which is the shortest honest route to this shape.
 */
export function toChisinauInput(epochSec: number): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Chisinau",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    // hourCycle h23 and NOT `hour12: false`: some ICU builds render midnight as
    // "24:00" under the latter, which no date input accepts.
    hourCycle: "h23",
  })
    .format(epochSec * 1000)
    .replace(" ", "T");
}

/**
 * The other direction: "2026-08-15T17:00" typed by a person in Moldova,
 * meaning 17.00 THERE, back to epoch seconds.
 *
 * A `datetime-local` value carries no timezone, so the browser would read it in
 * whatever zone the laptop is set to. That is right for a host in Chișinău and
 * wrong for the same person editing from a hotel in Bucharest — and wrong
 * silently, which is the part that matters.
 *
 * The offset is measured AT THE TARGET DATE rather than today, so an event
 * entered in January for August gets summer time. Moldova observes it, and the
 * README's rule is that the offset is never hardcoded.
 */
export function fromChisinauInput(local: string): number | null {
  const m = local.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;

  /*
   * Read the wall-clock digits as if they were UTC, then subtract the Chisinau
   * offset that applies AT THAT INSTANT.
   *
   * The two-pass shape is not belt-and-braces, it is the algorithm: the offset
   * has to be measured at the candidate instant, and the candidate depends on
   * the offset. One pass is wrong for a couple of hours either side of a DST
   * changeover; two passes settle it, because the offset can move by at most an
   * hour and the second measurement lands on the correct side of the boundary.
   *
   * The first version of this subtracted the offset INSIDE a loop, which double-
   * corrected: the offset never becomes zero, so the loop's exit condition never
   * fired and every iteration moved the answer another three hours. The DST
   * round-trip test below caught it, which is the reason it exists.
   */
  const target = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  let t = target - chisinauOffsetMs(target);
  t = target - chisinauOffsetMs(t);
  return Math.floor(t / 1000);
}

/** How far ahead of UTC Chisinau is at this instant, in milliseconds. */
function chisinauOffsetMs(utcMs: number): number {
  const shown = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Chisinau",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hourCycle: "h23",
  }).format(utcMs);
  const p = shown.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
  if (!p) return 0;
  return Date.UTC(+p[1]!, +p[2]! - 1, +p[3]!, +p[4]!, +p[5]!, +p[6]!) - utcMs;
}
